// src/pages/FiatExchange/hooks/useCoinBaseOfframpQuote.ts
import { useEffect } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { SellQuoteRequest } from '@src/types/coinbase'
import { createContextLogger } from '@src/utils'

import { cbGetOfframpQuote } from '../api/coinbase.api'

const logger = createContextLogger('useCoinBaseOfframpQuote')

export const useCoinBaseOfframpQuote = (
    params: SellQuoteRequest | null,
    options?: {
        isCoinbaseQuoteEnabled?: boolean
        coinbaseQuoteQueryKey?: unknown[]
    }
) => {
    const queryClient = useQueryClient()
    const isEnabled = options?.isCoinbaseQuoteEnabled ?? true
    const queryKey = options?.coinbaseQuoteQueryKey || ['coinbaseSellQuote', params ?? 'null']

    logger.debug('useCoinBaseOfframpQuote hook initialized with params:', {
        requestParams: params,
        isEnabled,
        queryKey: JSON.stringify(queryKey),
    })

    const query = useQuery({
        queryKey,
        queryFn: async () => {
            if (!params) {
                logger.warn('Cannot fetch offramp quote with null params')
                throw new Error('Offramp quote params are null')
            }

            try {
                logger.info('Fetching Coinbase offramp quote with params:', {
                    sell_currency: params.sell_currency,
                    sell_amount: params.sell_amount,
                    cashout_currency: params.cashout_currency,
                    payment_method: params.payment_method,
                    country: params.country,
                    sell_network: params.sell_network,
                })

                const startTime = performance.now()
                const quote = await cbGetOfframpQuote(params)
                const endTime = performance.now()

                logger.success('Coinbase offramp quote fetched successfully in', Math.round(endTime - startTime), 'ms')
                logger.debug('Quote details:', {
                    quote_id: quote.quote_id,
                    cashout_total: quote.cashout_total,
                    coinbase_fee: quote.coinbase_fee,
                    sell_amount: quote.sell_amount,
                })

                return quote
            } catch (error) {
                logger.error('Failed to fetch Coinbase offramp quote:', error)
                throw error
            }
        },
        enabled: isEnabled && !!params,
        refetchOnWindowFocus: false,
        staleTime: 30000, // 30 seconds
    })

    const forceRefetch = async () => {
        if (!params) {
            logger.warn('Cannot force refetch of offramp quote with null params')
            return
        }

        logger.info('Forcing refetch of Coinbase offramp quote for:', {
            sell_currency: params.sell_currency,
            cashout_currency: params.cashout_currency,
            sell_amount: params.sell_amount,
        })
        await queryClient.invalidateQueries({ queryKey })
        logger.debug('Query invalidated, refetch triggered')
    }

    // Log when query status changes
    useEffect(() => {
        if (query.isLoading) {
            logger.debug('Offramp quote query loading state active')
        }
        if (query.isFetching) {
            logger.debug('Offramp quote query fetching state active')
        }
        if (query.isError && query.error) {
            logger.error('Offramp quote query error state active:', query.error)
        }
        if (query.isSuccess && query.data) {
            logger.success('Offramp quote query completed successfully with data')
        }
    }, [query.isLoading, query.isFetching, query.isError, query.isSuccess, query.error, query.data])

    return {
        ...query,
        forceRefetch,
        isFetching: query.isFetching,
        isLoading: query.isLoading,
        error: query.error,
    }
}
